import { Box, Typography, Grid, Paper } from '@mui/material';
import React, { FC } from 'react';
import { Gridprops, CalendarDay, CalendarMonth, CalendarLegend } from '../../../models/organization';

type SharedCalendarProps = {
    months: CalendarMonth[]; // required
    year?: number; // optional, defaults to current year
    onDayClick?: (day: CalendarDay, monthIndex: number) => void;
    gridProps?: Gridprops;
    daySize?: number;
    spacing?: number;
    legend?: CalendarLegend[];
};

const SharedCalendar: FC<SharedCalendarProps> = ({
    months,
    year = new Date().getFullYear(),
    onDayClick,
    gridProps = { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 },
    daySize = 28,
    spacing = 0.25,
    legend,
}) => {
    // Fallback only, for callers that pass no totalDays. It parses monthName as an English month,
    // so a localized name yields NaN and renders no days at all - prefer totalDays. DD-3807.
    const getDaysInMonth = (monthName: string): number => {
        const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
        return new Date(year, monthIndex + 1, 0).getDate();
    };

    const handleDayClick = (day: CalendarDay, monthIndex: number) => {
        if (!day.disabled && onDayClick) {
            onDayClick(day, monthIndex);
        }
    };

    const renderMonth = (monthData: CalendarMonth, monthIndex: number) => {
        const { month, days, subtitle, totalDays } = monthData;
        const calculatedTotalDays = totalDays ?? getDaysInMonth(month);
        const daysToRender = Array.from({ length: calculatedTotalDays }, (_, i) => {
            const dayNum = i + 1;
            return days.find((d) => d.day === dayNum) || { day: dayNum, color: '#e0e0e0' };
        });

        return (
            <Grid item {...gridProps} key={`${month}-${monthIndex}`}>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ mb: 0.5, textTransform: 'capitalize', fontWeight: 600, fontSize: '0.8rem' }}
                    >
                        {month}
                        {subtitle && ` - ${subtitle}`}
                    </Typography>
                    <Grid container spacing={spacing} sx={{ maxWidth: daySize * 10 }}>
                        {daysToRender.map((day, dayIndex) => (
                            <Grid item key={`${day.day}-${dayIndex}`}>
                                <Paper
                                    sx={{
                                        width: daySize,
                                        height: daySize,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        // A day carrying a full `background` (a split holiday/
                                        // unavailable cell) wins over the flat colour.
                                        ...(day.background
                                            ? { background: day.background }
                                            : { backgroundColor: day.color || '#e0e0e0' }),
                                        color:
                                            day.textColor || (day.color && day.color !== '#e0e0e0' ? 'white' : 'black'),
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        cursor: onDayClick && !day.disabled ? 'pointer' : 'default',
                                        opacity: day.disabled ? 0.5 : 1,
                                        '&:hover':
                                            onDayClick && !day.disabled
                                                ? {
                                                      transform: 'scale(1.05)',
                                                      transition: 'transform 0.2s',
                                                  }
                                                : {},
                                    }}
                                    onClick={() => handleDayClick(day, monthIndex)}
                                >
                                    {day.day}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Grid>
        );
    };

    return (
        <Box>
            {legend && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {legend.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: item.color,
                                    borderRadius: 0.5,
                                }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <Grid container spacing={3}>
                {months.map(renderMonth)}
            </Grid>
        </Box>
    );
};

export default SharedCalendar;
